import { useEffect, useRef, useState } from 'react';

// Interactive, SEO-safe preloader: a teal particle constellation that reacts to
// the cursor/touch, with the <OBN/> monogram and a load progress bar. Shows once
// per browser session, auto-dismisses on window load (hard-capped so it can never
// trap the user), and renders as an overlay ABOVE the already-rendered page so
// crawlers still see the real content underneath.
const Preloader = () => {
  const [progress, setProgress] = useState(0);
  const [exiting, setExiting] = useState(false);
  const [hidden, setHidden] = useState(
    () => typeof window !== 'undefined' && sessionStorage.getItem('obn_preloaded') === '1'
  );
  const canvasRef = useRef(null);
  const mouse = useRef({ x: -9999, y: -9999 });

  // Particle constellation
  useEffect(() => {
    if (hidden) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let w, h, raf;

    const resize = () => {
      w = canvas.width = window.innerWidth;
      h = canvas.height = window.innerHeight;
    };
    resize();

    const COUNT = Math.max(28, Math.min(90, Math.floor(window.innerWidth / 18)));
    const pts = Array.from({ length: COUNT }, () => ({
      x: Math.random() * w,
      y: Math.random() * h,
      vx: (Math.random() - 0.5) * 0.45,
      vy: (Math.random() - 0.5) * 0.45,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      for (const p of pts) {
        const dx = p.x - mouse.current.x;
        const dy = p.y - mouse.current.y;
        const dist = Math.hypot(dx, dy);
        if (dist < 150 && dist > 0) {
          p.x += (dx / dist) * 1.6;
          p.y += (dy / dist) * 1.6;
        }
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > w) p.vx *= -1;
        if (p.y < 0 || p.y > h) p.vy *= -1;
      }
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const a = pts[i];
          const b = pts[j];
          const d = Math.hypot(a.x - b.x, a.y - b.y);
          if (d < 130) {
            ctx.strokeStyle = `rgba(0,255,209,${0.16 * (1 - d / 130)})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }
      for (const p of pts) {
        ctx.fillStyle = 'rgba(0,255,209,0.75)';
        ctx.beginPath();
        ctx.arc(p.x, p.y, 1.6, 0, Math.PI * 2);
        ctx.fill();
      }
      if (!reduce) raf = requestAnimationFrame(draw);
    };
    draw();

    const move = (e) => { mouse.current = { x: e.clientX, y: e.clientY }; };
    const touch = (e) => { if (e.touches[0]) mouse.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; };
    window.addEventListener('resize', resize);
    window.addEventListener('mousemove', move);
    window.addEventListener('touchmove', touch, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', move);
      window.removeEventListener('touchmove', touch);
    };
  }, [hidden]);

  // Fixed 5-second load: smooth 0 -> 100% then exit
  useEffect(() => {
    if (hidden) return;
    const DURATION = 5000;
    const start = Date.now();

    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      const pct = Math.min(100, (elapsed / DURATION) * 100);
      setProgress(pct);
      if (elapsed >= DURATION) {
        clearInterval(interval);
        setExiting(true);
        setTimeout(() => {
          sessionStorage.setItem('obn_preloaded', '1');
          setHidden(true);
        }, 750);
      }
    }, 40);

    return () => clearInterval(interval);
  }, [hidden]);

  // Lock scroll while visible (release as soon as the exit animation starts)
  useEffect(() => {
    if (hidden || exiting) {
      document.body.style.overflow = '';
      return;
    }
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [hidden, exiting]);

  if (hidden) return null;

  return (
    <div aria-hidden="true" data-testid="site-preloader" className={`obn-preloader${exiting ? ' obn-pre-exit' : ''}`}>
      <style>{`
        .obn-preloader{position:fixed;inset:0;z-index:99999;background:#000;display:flex;align-items:center;justify-content:center;overflow:hidden;transition:opacity .75s cubic-bezier(.7,0,.3,1),transform .75s cubic-bezier(.7,0,.3,1);}
        .obn-pre-exit{opacity:0;transform:translateY(-100%);pointer-events:none;}
        .obn-pre-canvas{position:absolute;inset:0;width:100%;height:100%;}
        .obn-pre-vignette{position:absolute;inset:0;background:radial-gradient(circle at 50% 50%,transparent 0%,rgba(0,0,0,.65) 75%);pointer-events:none;}
        .obn-pre-center{position:relative;z-index:2;display:flex;flex-direction:column;align-items:center;gap:1.4rem;padding:0 1.5rem;}
        .obn-pre-logo{font-family:'JetBrains Mono','Courier New',monospace;font-weight:700;font-size:clamp(2.75rem,9vw,5.5rem);letter-spacing:.04em;color:#fff;line-height:1;animation:obnGlow 2.4s ease-in-out infinite,obnRise .8s cubic-bezier(.2,.8,.2,1) both;}
        .obn-pre-logo .t{color:#00FFD1;}
        .obn-pre-tag{font-family:'JetBrains Mono','Courier New',monospace;font-size:.72rem;letter-spacing:.36em;text-transform:uppercase;color:rgba(255,255,255,.45);animation:obnFade .8s ease .35s both;}
        .obn-pre-track{width:min(280px,64vw);height:2px;background:rgba(255,255,255,.1);overflow:hidden;animation:obnFade .8s ease .5s both;}
        .obn-pre-fill{height:100%;background:#00FFD1;box-shadow:0 0 14px rgba(0,255,209,.9);transition:width .12s linear;}
        .obn-pre-pct{font-family:'JetBrains Mono','Courier New',monospace;font-size:.72rem;letter-spacing:.15em;color:#00FFD1;animation:obnFade .8s ease .5s both;}
        @keyframes obnGlow{0%,100%{text-shadow:0 0 22px rgba(0,255,209,.28)}50%{text-shadow:0 0 44px rgba(0,255,209,.7)}}
        @keyframes obnRise{from{opacity:0;transform:translateY(14px) scale(.96)}to{opacity:1;transform:none}}
        @keyframes obnFade{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
        @media (prefers-reduced-motion: reduce){.obn-pre-logo{animation:obnFade .4s ease both}}
      `}</style>
      <canvas ref={canvasRef} className="obn-pre-canvas" />
      <div className="obn-pre-vignette" />
      <div className="obn-pre-center">
        <div className="obn-pre-logo"><span className="t">&lt;</span>OBN<span className="t">/&gt;</span></div>
        <div className="obn-pre-tag">Digital Transformation &amp; AI</div>
        <div className="obn-pre-track"><div className="obn-pre-fill" style={{ width: `${progress}%` }} /></div>
        <div className="obn-pre-pct">{Math.round(progress)}%</div>
      </div>
    </div>
  );
};

export default Preloader;
